import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { adminLeagueManagementAPI, adminTeamsAPI, isBenignClientAbort } from '../services/adminApi';
import { useDivisionLockStatus, DivisionLockWarning } from '../hooks/useDivisionLock';
import { isOptimalGroupSize, SPLIT_AVAILABLE_FROM, SUPPORTED_GROUP_SIZES_LABEL } from '../constants/leagueGroupSizes';
import AdminTeamMemberProfileCard, { AdminTeamMembersGrid } from './AdminTeamMemberProfileCard';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const DivisionAccordion = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 1rem;
  background: white;
  overflow: hidden;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const DivisionHeader = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: ${props => props.$isExpanded ? '#f9fafb' : 'white'};
  transition: background 0.2s ease;
  
  &:hover {
    background: #f9fafb;
  }
`;

const DivisionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DivisionName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const SkillRange = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
`;

const ExpandIcon = styled.span`
  font-size: 1.25rem;
  color: #6b7280;
  transition: transform 0.2s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DivisionContent = styled.div`
  max-height: ${props => props.$isExpanded ? 'none' : '0'};
  opacity: ${props => props.$isExpanded ? '1' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
  padding: ${props => props.$isExpanded ? '1.5rem' : '0 1.5rem'};
  background: #fafafa;
`;

const CantonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const CantonCard = styled.div`
  background: white;
  border: 2px solid ${props => {
    if (props.$isGroupDivision) {
      return '#8b5cf6'; // Purple for group divisions
    }
    return isOptimalGroupSize(props.$teamCount)
      ? '#10b981' 
      : props.$teamCount === 0 
        ? '#e5e7eb' 
        : '#f59e0b';
  }};
  border-radius: 12px;
  padding: 1.25rem;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  
  ${props => props.$isDragOver && `
    background: #dbeafe;
    border-color: #3b82f6;
    border-style: dashed;
  `}
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

const CantonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const CantonName = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const TeamCountBadge = styled.span`
  background: ${props => {
    // For group divisions, use purple border but still show optimal colors
    if (props.$isGroupDivision) {
      return isOptimalGroupSize(props.$teamCount)
        ? '#10b981' // Green for optimal group divisions
        : props.$teamCount === 0 
          ? '#9ca3af' // Gray for empty groups
          : '#f59e0b'; // Orange for non-optimal groups
    }
    // Regular divisions
    return isOptimalGroupSize(props.$teamCount)
      ? '#10b981' 
      : props.$teamCount === 0 
        ? '#9ca3af' 
        : '#f59e0b';
  }};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  border: ${props => props.$isGroupDivision ? '2px solid #8b5cf6' : 'none'};
`;


const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 60px;
  flex: 1;
  overflow-y: auto;
`;

const TeamItem = styled.div`
  padding: 0.75rem;
  background: ${props => props.$isDragging ? '#f3f4f6' : '#f9fafb'};
  border: 1px solid ${props => props.$isDragging ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  cursor: grab;
  transition: all 0.2s ease;
  opacity: ${props => props.$isDragging ? '0.5' : '1'};
  
  &:hover {
    background: #f3f4f6;
    border-color: #043663;
    transform: translateX(4px);
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const TeamName = styled.div`
  font-weight: 500;
  color: #1f2937;
  font-size: 0.875rem;
`;

const TeamEmail = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: ${props => props.$type === 'success' ? '#d1fae5' : props.$type === 'error' ? '#fee2e2' : '#fef3c7'};
  color: ${props => props.$type === 'success' ? '#065f46' : props.$type === 'error' ? '#991b1b' : '#92400e'};
  /* Stay visible while scrolled into a skill-band accordion */
  position: sticky;
  top: 0;
  z-index: 30;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  white-space: pre-wrap;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #6b7280;
`;

// Team Details Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const TeamInfoSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const TeamInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 0.875rem;
    color: #1f2937;
    font-weight: 500;
  }
`;

const MembersSection = styled.div`
  margin-top: 2rem;
`;

const MembersTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const MemberCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MemberAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.125rem;
`;

const MemberBasicInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const MemberRole = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const MemberDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MemberDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MemberDetailLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const MemberDetailValue = styled.span`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
`;

const SkillLevelBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const DivisionBadge = styled.span`
  background: #dbeafe;
  color: #043663;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const EmptyContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const DivisionAccordionManagement = ({ selectedSeason }) => {
  const [expandedDivisions, setExpandedDivisions] = useState(new Set());
  const [draggedTeam, setDraggedTeam] = useState(null);
  const [dragOverCanton, setDragOverCanton] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDetails, setShowTeamDetails] = useState(false);
  const [showSplitGroupsModal, setShowSplitGroupsModal] = useState(false);
  const [selectedCantonForSplit, setSelectedCantonForSplit] = useState(null);
  const [numberOfGroups, setNumberOfGroups] = useState(2);
  const [showUnsplitModal, setShowUnsplitModal] = useState(false);
  const [selectedParentForUnsplit, setSelectedParentForUnsplit] = useState(null);
  const queryClient = useQueryClient();
  const draggedTeamRef = useRef(null);
  const suppressClickAfterDragRef = useRef(false);
  const messageRef = useRef(null);

  const showMoveFeedback = (type, text) => {
    setMessage({ type, text });
    if (type === 'error') {
      toast.error(text, { duration: 8000 });
    } else if (type === 'success') {
      toast.success(text, { duration: 3000 });
    }
    requestAnimationFrame(() => {
      messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  // Check division lock status for the season being balanced
  const { isLocked: isDivisionLocked } = useDivisionLockStatus(null, selectedSeason || null);

  // Fetch divisions grouped by level
  // Disable auto-refresh when viewing team details to prevent overwriting
  const { data, isLoading, error } = useQuery(
    ['divisions-by-level', selectedSeason],
    () => adminLeagueManagementAPI.getDivisionsByLevel(selectedSeason || ''),
    {
      retry: false,
      refetchInterval:
        showTeamDetails || showSplitGroupsModal || showUnsplitModal ? false : 60000
    }
  );

  // Fetch detailed team data when a team is selected
  // Disable auto-refresh when viewing team details to prevent overwriting
  const { data: teamDetailsData, isLoading: teamDetailsLoading } = useQuery(
    ['teamDetails', selectedTeam?.id, selectedSeason],
    () => adminTeamsAPI.getTeamDetails(selectedTeam?.id, { seasonId: selectedSeason || undefined }),
    {
      enabled: !!selectedTeam,
      refetchInterval: showTeamDetails ? false : 60000 // Disabled when team details modal is open
    }
  );

  // Move team mutation
  const moveTeamMutation = useMutation(
    ({ teamId, fromDivisionId, toDivisionId }) =>
      adminLeagueManagementAPI.moveTeam(teamId, fromDivisionId, toDivisionId, selectedSeason || undefined, 'Moved via accordion view'),
    {
      onSuccess: (data) => {
        if (data && data.success === false) {
          showMoveFeedback('error', data.message || 'Failed to move team');
          return;
        }
        showMoveFeedback('success', 'Team moved successfully! Matches will be regenerated.');
        // Invalidate all relevant queries to update all views
        queryClient.invalidateQueries(['divisions-by-level', selectedSeason]);
        queryClient.invalidateQueries(['divisions-overview', selectedSeason]);
        queryClient.invalidateQueries('cantonDivisions'); // Canton Detail divisions
        queryClient.invalidateQueries('cantonTeams'); // Canton Detail teams
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      },
      onError: (error) => {
        if (isBenignClientAbort(error)) return;
        const data = error.response?.data;
        const text = data?.message || 'Failed to move team';
        showMoveFeedback('error', text);
        setTimeout(() => setMessage({ type: '', text: '' }), 10000);
      }
    }
  );


  const toggleDivision = (levelKey) => {
    const newExpanded = new Set(expandedDivisions);
    if (newExpanded.has(levelKey)) {
      newExpanded.delete(levelKey);
    } else {
      newExpanded.add(levelKey);
    }
    setExpandedDivisions(newExpanded);
  };

  const handleDragStart = (e, team, fromDivisionId) => {
    // Prevent drag if divisions are locked
    if (isDivisionLocked) {
      e.preventDefault();
      showMoveFeedback('error', 'Cannot move teams while divisions are locked!');
      return;
    }

    const payload = { ...team, fromDivisionId };
    draggedTeamRef.current = payload;
    suppressClickAfterDragRef.current = true;
    setDraggedTeam(payload);
    try {
      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({ id: team.id, fromDivisionId })
      );
    } catch (_) {
      /* some browsers restrict setData types */
    }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    draggedTeamRef.current = null;
    setDraggedTeam(null);
    setDragOverCanton(null);
    // Allow the post-drag click to be ignored, then re-enable normal clicks
    setTimeout(() => {
      suppressClickAfterDragRef.current = false;
    }, 150);
  };

  const handleDragOver = (e, toDivisionId) => {
    e.preventDefault();
    
    // Prevent drag over if divisions are locked
    if (isDivisionLocked) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    setDragOverCanton(toDivisionId);
  };

  const handleDragLeave = () => {
    setDragOverCanton(null);
  };

  const handleDrop = (e, toDivisionId) => {
    e.preventDefault();
    setDragOverCanton(null);
    
    // Prevent drop if divisions are locked
    if (isDivisionLocked) {
      showMoveFeedback('error', 'Cannot move teams while divisions are locked!');
      return;
    }

    let payload = draggedTeamRef.current || draggedTeam;
    if (!payload?.id) {
      try {
        const raw = e.dataTransfer.getData('application/json');
        if (raw) payload = JSON.parse(raw);
      } catch (_) {
        payload = null;
      }
    }

    if (!payload?.id) {
      showMoveFeedback('error', 'Could not complete the move — please try dragging the team again.');
      return;
    }
    
    // Don't move if dropping in the same division
    if (payload.fromDivisionId === toDivisionId) {
      return;
    }
    
    moveTeamMutation.mutate({
      teamId: payload.id,
      fromDivisionId: payload.fromDivisionId,
      toDivisionId
    });
  };

  const handleTeamClick = (team) => {
    if (suppressClickAfterDragRef.current) {
      suppressClickAfterDragRef.current = false;
      return;
    }
    handleViewTeamDetails(team);
  };

  const handleViewTeamDetails = (team) => {
    setSelectedTeam(team);
    setShowTeamDetails(true);
  };

  const handleCloseTeamDetails = () => {
    setShowTeamDetails(false);
    setSelectedTeam(null);
  };

  // Lock body scroll when team details modal is open
  useEffect(() => {
    if (showTeamDetails) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      // Get the scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Lock the body scroll without affecting fixed elements like sidebar
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Also add padding to the sidebar to prevent it from shifting
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        sidebar.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // Restore scroll when modal closes
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        if (sidebar) {
          sidebar.style.paddingRight = '';
        }
        window.scrollTo(0, scrollY);
      };
    }
  }, [showTeamDetails]);

  // Split groups mutation
  const splitGroupsMutation = useMutation(
    ({ divisionId, numberOfGroups }) =>
      adminLeagueManagementAPI.splitGroups(divisionId, numberOfGroups, selectedSeason || undefined),
    {
      retry: false,
      onSuccess: (response) => {
        console.log('Split groups response:', response);
        setMessage({ 
          type: 'success', 
          text: `Successfully split into ${numberOfGroups} groups!` 
        });
        
        // Invalidate all relevant queries to update all views
        queryClient.invalidateQueries(['divisions-by-level', selectedSeason]);
        queryClient.invalidateQueries(['divisions-overview', selectedSeason]);
        queryClient.invalidateQueries('cantonDivisions');
        queryClient.invalidateQueries('cantonTeams');
        handleCloseSplitGroupsModal();
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      },
      onError: (error) => {
        if (isBenignClientAbort(error)) return;
        setMessage({ 
          type: 'error', 
          text:
            error.response?.data?.message ||
            (error.message && /timeout/i.test(error.message)
              ? 'The request took too long (try again, or check if the split completed and refresh the page).'
              : 'Failed to split groups')
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    }
  );

  const handleSplitGroups = (canton) => {
    setSelectedCantonForSplit(canton);
    setShowSplitGroupsModal(true);
  };

  const handleCloseSplitGroupsModal = () => {
    if (splitGroupsMutation.isLoading) return;
    setShowSplitGroupsModal(false);
    setSelectedCantonForSplit(null);
    setNumberOfGroups(2);
  };

  const handleSplitGroupsSubmit = () => {
    if (selectedCantonForSplit) {
      splitGroupsMutation.mutate({
        divisionId: selectedCantonForSplit.divisionId,
        numberOfGroups: numberOfGroups
      });
    }
  };

  // Unsplit groups mutation
  const unsplitGroupsMutation = useMutation(
    ({ parentDivisionId, seasonId }) =>
      adminLeagueManagementAPI.unsplitGroups(parentDivisionId, seasonId),
    {
      retry: false,
      onSuccess: (response) => {
        console.log('Unsplit groups response:', response);
        setMessage({ 
          type: 'success', 
          text: `Successfully unsplit groups!` 
        });
        
        // Invalidate all relevant queries to update all views
        queryClient.invalidateQueries(['divisions-by-level', selectedSeason]);
        queryClient.invalidateQueries(['divisions-overview', selectedSeason]);
        queryClient.invalidateQueries('cantonDivisions');
        queryClient.invalidateQueries('cantonTeams');
        handleCloseUnsplitModal();
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      },
      onError: (error) => {
        if (isBenignClientAbort(error)) return;
        setMessage({ 
          type: 'error', 
          text:
            error.response?.data?.message ||
            (error.message && /timeout/i.test(error.message)
              ? 'The request took too long (try again, or check if the operation completed and refresh the page).'
              : 'Failed to unsplit groups')
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    }
  );

  const handleUnsplitGroups = (parentDivision) => {
    setSelectedParentForUnsplit(parentDivision);
    setShowUnsplitModal(true);
  };

  const handleCloseUnsplitModal = () => {
    setShowUnsplitModal(false);
    setSelectedParentForUnsplit(null);
  };

  const handleUnsplitGroupsSubmit = () => {
    if (selectedParentForUnsplit) {
      unsplitGroupsMutation.mutate({
        parentDivisionId: selectedParentForUnsplit.parentDivisionId,
        seasonId: selectedSeason || undefined
      });
    }
  };


  if (isLoading) {
    return <LoadingSpinner>Loading divisions...</LoadingSpinner>;
  }

  if (error) {
    return (
      <Container>
        <Message $type="error">
          Error loading divisions: {error.message}
        </Message>
      </Container>
    );
  }

  const divisions = data?.data?.divisions || [];

  // Calculate total teams shown in accordion
  const totalTeamsInAccordion = divisions.reduce((sum, division) => {
    return sum + division.cantons.reduce((cantonSum, canton) => {
      return cantonSum + (canton.teams?.length || 0);
    }, 0);
  }, 0);

  return (
    <Container>
      <DivisionLockWarning>
        <Title>🎯 Division-Based Team Management</Title>
        <Description>
          Expand any division to see all cantons and teams within that skill level. 
          Drag teams between cantons to balance divisions. <strong>Target: {SUPPORTED_GROUP_SIZES_LABEL} teams per canton</strong> for match scheduling.
          <br />
          <span style={{ color: '#8b5cf6' }}>🔀 Group divisions</span> are created when cantons have too many teams and can be managed independently.
          <br />
          <strong style={{ color: '#dc2626' }}>⚠️ Note:</strong> Only teams assigned to divisions are shown here. Unassigned teams will NOT be included in match generation. Check the stats above for unassigned team count.
          <br />
          <span style={{ color: '#059669', fontWeight: '600' }}>Teams shown in accordion: {totalTeamsInAccordion}</span>
        </Description>
      </DivisionLockWarning>

      {message.text ? (
        <Message ref={messageRef} $type={message.type}>
          {message.text}
        </Message>
      ) : null}

      {divisions.length === 0 ? (
        <EmptyState>No divisions found for this season</EmptyState>
      ) : (
        divisions.map((division) => {
          const levelKey = `${division.skillMin}-${division.skillMax}`;
          const isExpanded = expandedDivisions.has(levelKey);

          return (
            <DivisionAccordion key={levelKey}>
              <DivisionHeader
                $isExpanded={isExpanded}
                onClick={() => toggleDivision(levelKey)}
              >
                <DivisionInfo>
                  <DivisionName>
                    {division.displayName} 
                  </DivisionName>
                  <SkillRange>{division.skillMin} - {division.skillMax}</SkillRange>
                </DivisionInfo>
                <ExpandIcon $isExpanded={isExpanded}>▼</ExpandIcon>
              </DivisionHeader>

              <DivisionContent $isExpanded={isExpanded}>
                <CantonGrid>
                  {division.cantons.map((canton) => (
                    <CantonCard
                      key={canton.divisionId}
                      $teamCount={canton.teamCount}
                      $isGroupDivision={canton.isGroupDivision}
                      $isDragOver={dragOverCanton === canton.divisionId}
                      onDragOver={(e) => handleDragOver(e, canton.divisionId)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, canton.divisionId)}
                    >
                      <CantonHeader>
                        <CantonName>
                          {canton.isGroupDivision ? canton.divisionName : canton.canton}
                          {canton.isGroupDivision && <span style={{ color: '#8b5cf6', fontSize: '0.8em', marginLeft: '0.5rem' }}>🔀</span>}
                        </CantonName>
                        <TeamCountBadge $teamCount={canton.teamCount} $isGroupDivision={canton.isGroupDivision}>
                          {canton.teamCount} 
                          {canton.isGroupDivision 
                            ? (isOptimalGroupSize(canton.teamCount) ? ' 🔀✅' : ' 🔀')
                            : (isOptimalGroupSize(canton.teamCount) ? ' ✅' : '')
                          }
                        </TeamCountBadge>
                      </CantonHeader>

                      <TeamList>
                        {canton.teams.length === 0 ? (
                          <EmptyState style={{ padding: '1rem' }}>No teams</EmptyState>
                        ) : (
                          canton.teams.map((team) => (
                            <TeamItem
                              key={team.id}
                              draggable={!isDivisionLocked}
                              $isDragging={draggedTeam?.id === team.id}
                              onDragStart={(e) => handleDragStart(e, team, canton.divisionId)}
                              onDragEnd={handleDragEnd}
                              onClick={() => handleTeamClick(team)}
                              style={{ 
                                cursor: isDivisionLocked ? 'not-allowed' : 'pointer',
                                opacity: isDivisionLocked ? 0.6 : 1
                              }}
                            >
                              <TeamName>{team.team_name}</TeamName>
                              <TeamEmail>{team.email}</TeamEmail>
                            </TeamItem>
                          ))
                        )}
                      </TeamList>
                      
                      {/* Split Groups Button — optional from 10 teams (incl. 12/14); not on group rows */}
                      {canton.teamCount >= SPLIT_AVAILABLE_FROM && !canton.isGroupDivision && (
                        <button
                          onClick={() => handleSplitGroups(canton)}
                          style={{
                            background: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            marginTop: '0.5rem',
                            width: '100%'
                          }}
                          title={`Split ${canton.canton} into groups (${canton.teamCount} teams)`}
                        >
                          🔀 Split into Groups
                        </button>
                      )}

                      {/* Unsplit Groups Button - Show when this is a group division */}
                      {canton.isGroupDivision && canton.parentDivisionId && (
                        <button
                          onClick={() => handleUnsplitGroups({ parentDivisionId: canton.parentDivisionId, parentName: canton.canton })}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            marginTop: '0.5rem',
                            width: '100%'
                          }}
                          title={`Unsplit groups back to ${canton.canton}`}
                        >
                          ↩️ Unsplit Groups
                        </button>
                      )}
                    </CantonCard>
                  ))}
                </CantonGrid>
              </DivisionContent>
            </DivisionAccordion>
          );
        })
      )}

      {/* Team Details Modal */}
      {showTeamDetails && selectedTeam && (
        <ModalOverlay onClick={handleCloseTeamDetails}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Team Details - {selectedTeam.team_name}</ModalTitle>
              <CloseButton onClick={handleCloseTeamDetails}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <TeamInfoSection>
                <TeamInfoGrid>
                  <InfoItem>
                    <div className="label">Team Name</div>
                    <div className="value">{selectedTeam.team_name}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Canton</div>
                    <div className="value">{selectedTeam.canton}</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Skill Level</div>
                    <div className="value">
                      <SkillLevelBadge>{selectedTeam.team_skill_level || selectedTeam.skill_level_min}</SkillLevelBadge>
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Division</div>
                    <div className="value">
                      <DivisionBadge>{teamDetailsData?.data?.team?.division_name || 'Not assigned'}</DivisionBadge>
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Member Count</div>
                    <div className="value">{selectedTeam.member_count || 0}/2</div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Team Phone Number</div>
                    <div className="value">
                      {teamDetailsData?.data?.team?.phone_number || 'Not provided'}
                    </div>
                  </InfoItem>
                  <InfoItem>
                    <div className="label">Joined</div>
                    <div className="value">
                      {selectedTeam.created_at ? new Date(selectedTeam.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </InfoItem>
                </TeamInfoGrid>
              </TeamInfoSection>

              <MembersSection>
                <MembersTitle>Team Members</MembersTitle>
                {teamDetailsLoading ? (
                  <LoadingContainer>Loading team members...</LoadingContainer>
                ) : teamDetailsData?.data?.members ? (
                  <AdminTeamMembersGrid>
                    {teamDetailsData.data.members.map((member) => (
                      <AdminTeamMemberProfileCard
                        key={member.id}
                        member={member}
                        teamId={selectedTeam?.id}
                        seasonId={selectedSeason || undefined}
                        onPaymentUpdated={() =>
                          queryClient.invalidateQueries(['teamDetails', selectedTeam?.id])
                        }
                      />
                    ))}
                  </AdminTeamMembersGrid>
                ) : (
                  <EmptyContainer>
                    No team members found
                  </EmptyContainer>
                )}
              </MembersSection>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Split Groups Modal */}
      {showSplitGroupsModal && selectedCantonForSplit && (
        <ModalOverlay
          onClick={() => {
            if (!splitGroupsMutation.isLoading) handleCloseSplitGroupsModal();
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Split {selectedCantonForSplit.canton} into Groups</ModalTitle>
              <CloseButton onClick={handleCloseSplitGroupsModal}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <div style={{ marginBottom: '1rem' }}>
                <p>
                  <strong>{selectedCantonForSplit.canton}</strong> currently has <strong>{selectedCantonForSplit.teamCount} teams</strong>.
                </p>
                <p>
                  You can split these teams into multiple groups (A, B, C, etc.) to create separate divisions for better match scheduling.
                </p>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#1f2937' }}>
                  How many groups do you want to create?
                </label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={numberOfGroups}
                  onChange={(e) => setNumberOfGroups(parseInt(e.target.value) || 2)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem'
                  }}
                  placeholder="Enter number of groups (2-10)"
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  All teams will initially be placed in Group A. You can then drag and drop teams between groups.
                  <br />
                  <strong>Recommended:</strong> 2-4 groups for optimal match scheduling.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCloseSplitGroupsModal}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSplitGroupsSubmit}
                  disabled={splitGroupsMutation.isLoading}
                  style={{
                    background: splitGroupsMutation.isLoading ? '#9ca3af' : '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: splitGroupsMutation.isLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {splitGroupsMutation.isLoading ? 'Creating Groups...' : 'Create Groups'}
                </button>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Unsplit Groups Modal */}
      {showUnsplitModal && selectedParentForUnsplit && (
        <ModalOverlay onClick={handleCloseUnsplitModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Unsplit Groups</ModalTitle>
              <CloseButton onClick={handleCloseUnsplitModal}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <div style={{ marginBottom: '1rem' }}>
                <p>
                  Are you sure you want to unsplit the groups for <strong>{selectedParentForUnsplit.parentName}</strong>?
                </p>
                <p>
                  This will:
                </p>
                <ul style={{ marginLeft: '1rem', color: '#6b7280' }}>
                  <li>Move all teams back to the original division</li>
                  <li>Delete all group divisions</li>
                  <li>Restore the original team distribution</li>
                </ul>
                <p style={{ color: '#ef4444', fontWeight: '500' }}>
                  ⚠️ This action cannot be undone!
                </p>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCloseUnsplitModal}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnsplitGroupsSubmit}
                  disabled={unsplitGroupsMutation.isLoading}
                  style={{
                    background: unsplitGroupsMutation.isLoading ? '#9ca3af' : '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: unsplitGroupsMutation.isLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {unsplitGroupsMutation.isLoading ? 'Unsplit Groups...' : 'Unsplit Groups'}
                </button>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

    </Container>
  );
};

export default DivisionAccordionManagement;

